/**

 * Enforcer editorial — humanidades (lenguaje, historia).

 * Convierte pseudo-fórmulas algebraicas, limpia LaTeX crudo y refuerza bloques legibles.

 */



import {

  humanizeConceptFormulaText,

  isHumanitiesFormulaSubject,

} from '../../activities/guide_editorial/guide_editorial.logic';

import { resolveAuthenticEditorialTheme } from './genreStimulusEnforcer';

import type { StimulusTheme } from '../types/payloads/stimulus.types';



export interface GuideBlockLike {

  mode: string;

  payload?: Record<string, unknown>;

  label?: string;

}



const LATEX_LINE_RE =
  /\\frac|\\Delta|\\FRAC|\\DELTA|\\times|\\TIMES|\$\s*[^$]+\$|\\text\{|\\mathrm\{|F_\{|_\{|\\[a-zA-Z]+\{|\^[_{]/i;

const PSEUDO_MATH_TOKEN_RE =
  /\\frac|\\FRAC|\\times|\\TIMES|\\cdot|\\CDOT|\\sqrt|\\sum|_\{|\^\{|\$[^$]+\$|\\[a-zA-Z]+\{/gi;

const PEDAGOGICAL_ANALOGY_RE = /analog[ií]a\s+pedag[oó]gica|pseudo[\s-]?f[oó]rmula|f[oó]rmula\s+conceptual/i;



const ANATOMY_PREFIX_RE = /Anatom[ií]a de [^:\n]+:\s*/gi;



function containsHumanitiesLatex(text: string): boolean {
  const blob = String(text || '').trim();
  if (!blob) return false;
  return LATEX_LINE_RE.test(blob) || PSEUDO_MATH_TOKEN_RE.test(blob) || /_[a-zA-Z0-9{]/.test(blob);
}

function stripPseudoMathTokens(text: string): string {
  return String(text || '')
    .split('\n')
    .filter((line) => !PEDAGOGICAL_ANALOGY_RE.test(line) && !LATEX_LINE_RE.test(line.trim()))
    .join('\n')
    .replace(PSEUDO_MATH_TOKEN_RE, '')
    .replace(/_\{([^}]*)\}/g, ' $1')
    .replace(/\^\{([^}]*)\}/g, ' $1')
    .replace(/\{([^{}]*)\}/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function humanizeHumanitiesText(text: string): string {
  return stripPseudoEquationNotation(stripPseudoMathTokens(stripLatexLines(humanizeConceptFormulaText(text))));
}



const ALGEBRAIC_CONCEPT_RE = /^(.+?)\s*=\s*(.+)$/;



function stripLatexLines(body: string): string {

  return body

    .split('\n')

    .filter((line) => {

      const t = line.trim();

      if (!t) return true;

      return !LATEX_LINE_RE.test(t);

    })

    .join('\n')

    .replace(/\n{3,}/g, '\n\n')

    .trim();

}



function stripPseudoEquationNotation(text: string): string {

  return String(text || '')

    .replace(ANATOMY_PREFIX_RE, '')

    .replace(/\s*=\s*/g, ' — ')

    .replace(/\s*\+\s*/g, ', ')

    .replace(/\n{3,}/g, '\n\n')

    .trim();

}



function parseConceptParts(raw: string): { label: string; parts: string[] } | null {

  const text = humanizeConceptFormulaText(raw.replace(/^\$|\$$/g, '').trim());

  const match = text.match(ALGEBRAIC_CONCEPT_RE);

  if (!match) return null;



  const label = match[1]!.trim();

  const rhs = match[2]!.trim();

  if (!label || !rhs) return null;



  const parts = rhs

    .split(/\s*\+\s*/)

    .map((p) => p.trim())

    .filter(Boolean);



  if (parts.length < 2) return null;

  if (/\\frac|\\sqrt|\\sum/i.test(text)) return null;



  return { label, parts };

}



function formulaToPlainEditorialBlock(block: GuideBlockLike): GuideBlockLike {

  const payload = (block.payload ?? {}) as Record<string, unknown>;

  const latex = String(payload.latex || payload.formula || '').trim();

  const description = String(payload.description || '').trim();

  const title = String(payload.title || block.label || 'Elementos clave').trim();



  const parsed = parseConceptParts(latex);

  const humanDesc = description ? humanizeHumanitiesText(description) : '';

  const humanLatex = latex ? humanizeHumanitiesText(latex) : '';



  if (parsed) {

    const bullets = parsed.parts.map((part) => `• ${humanizeConceptFormulaText(part)}`).join('\n');

    const body = [humanDesc, bullets].filter(Boolean).join('\n\n');

    return {

      ...block,

      mode: 'destacado',

      label: title,

      payload: {

        title: title || parsed.label,

        body,

        tone: 'neutral',

        editorialTheme: resolveAuthenticEditorialTheme(title, body),

      },

    };

  }



  const body = [humanDesc, humanLatex ? stripPseudoEquationNotation(humanLatex) : '']

    .filter(Boolean)

    .join('\n\n');



  return {

    ...block,

    mode: 'destacado',

    label: title,

    payload: {

      title,

      body: body || title,

      tone: 'neutral',

      editorialTheme: resolveAuthenticEditorialTheme(title, body || title),

    },

  };

}



function inferDestacadoTheme(payload: Record<string, unknown>): StimulusTheme | undefined {

  const title = String(payload.title ?? '');

  const body = String(payload.body ?? '');

  return resolveAuthenticEditorialTheme(title, body);

}



export function normalizeTextForCompare(text: string): string {

  return String(text || '')

    .toLowerCase()

    .replace(/\s+/g, ' ')

    .trim();

}



export function isDuplicateBody(text: string, existing: Set<string>): boolean {

  const norm = normalizeTextForCompare(text);

  if (!norm) return true;

  if (existing.has(norm)) return true;

  for (const other of existing) {

    if (other.includes(norm) || norm.includes(other)) {

      if (Math.min(other.length, norm.length) / Math.max(other.length, norm.length) > 0.85) {

        return true;

      }

    }

  }

  return false;

}



const ZOMBIE_DESTACADO_RE =

  /dato clave para comprender|recuerda:\s*(?:que\s+)?(?:sirva|permita)|idea clave:\s*(?:que\s+)?(?:sirva|permita)|(?:que\s+)?(?:sirva|sirvan|permita|ayude)\s+para\s+comprender/i;



const NEUTRAL_NEWS_IDEA_BODY =
  'Una noticia informa hechos. El titular resume; la bajada amplía; el lead responde quién, qué, cuándo, dónde y por qué; el cuerpo detalla sin opiniones.';



function scrubZombieDestacado<T extends GuideBlockLike>(block: T): T {

  if (block.mode !== 'destacado') return block;

  const payload = { ...(block.payload as Record<string, unknown>) };

  const title = String(payload.title || '');

  const body = String(payload.body || '');

  const label = String(block.label || '');

  const blob = `${title}\n${body}\n${label}`;

  if (!ZOMBIE_DESTACADO_RE.test(blob)) return block;

  return {

    ...block,

    label: 'Idea clave',

    payload: {

      ...payload,

      title: 'Idea clave',

      body: NEUTRAL_NEWS_IDEA_BODY,

      tone: 'neutral',

    },

  };

}



/** Colapsa destacados idénticos o casi idénticos (Idea clave ×2/×3). */

export function dedupeDestacadoBlocks<T extends GuideBlockLike>(blocks: T[]): T[] {

  const orphanTail =

    /¿Qué idea o dato de este texto vas a usar en la actividad siguiente\?.*?$/i;

  const seen = new Set<string>();

  const out: T[] = [];

  for (const block of blocks) {

    if (block.mode !== 'destacado') {

      out.push(block);

      continue;

    }

    const payload = (block.payload || {}) as Record<string, unknown>;

    const bodyCore = String(payload.body || '').replace(orphanTail, '').trim();

    const fingerprint = normalizeTextForCompare(

      `${String(payload.title || '').replace(/^Recuerda:\s*/i, '')}\n${bodyCore}`,

    );

    if (isDuplicateBody(fingerprint, seen)) continue;

    seen.add(fingerprint);

    out.push(block);

  }

  return out;

}



export function stripHumanitiesLatexFromBlocks<T extends GuideBlockLike>(blocks: T[]): T[] {

  return blocks.map((block) => {

    const payload = { ...(block.payload as Record<string, unknown>) };



    if (block.mode === 'info_block') {

      const cleaned = humanizeHumanitiesText(String(payload.bodyText || ''));

      const nextPayload = { ...payload, bodyText: cleaned };

      delete nextPayload.formulaLayout;

      delete nextPayload.formula;

      delete nextPayload.latex;

      if (cleaned === payload.bodyText && !payload.formulaLayout && !payload.formula && !payload.latex) {

        return block;

      }

      return { ...block, payload: nextPayload };

    }



    if (block.mode === 'destacado') {

      const cleaned = humanizeHumanitiesText(String(payload.body || ''));

      if (cleaned === payload.body) return block;

      return { ...block, payload: { ...payload, body: cleaned } };

    }



    return block;

  });

}



export function enforceHumanitiesGuideBlocks<T extends GuideBlockLike>(

  blocks: T[],

  subjectTag?: string,

): T[] {

  if (!isHumanitiesFormulaSubject(subjectTag)) return blocks;



  let next = stripHumanitiesLatexFromBlocks(blocks);



  next = next.map((block) => {

    if (block.mode === 'formula_destacada') {

      return formulaToPlainEditorialBlock(block) as T;

    }

    return block;

  });



  next = next.map((block) => {

    if (block.mode !== 'destacado') return block;

    const scrubbed = scrubZombieDestacado(block);

    const payload = { ...(scrubbed.payload as Record<string, unknown>) };

    const theme = String(payload.editorialTheme || '') || inferDestacadoTheme(payload);

    return {

      ...scrubbed,

      payload: {

        ...payload,

        tone: 'neutral',

        ...(theme && theme !== 'prose' ? { editorialTheme: theme } : {}),

      },

    };

  });



  return dedupeDestacadoBlocks(next);

}



export { stripLatexLines, parseConceptParts };


